import { SortParamEnum, SortParamType } from '@shared/Enums/SortParamEnum';
import { ItemTypeEntity } from '@entities/ItemType/Types';
import { notifySuccessCustom } from '@shared/Widgets/ToastNotifications/ToastNotifications';
import { ChangeEvent, useEffect, useState } from 'react';
import css from './SelectionItemsHeaderMobile.module.css';
import { ArrowIcon } from '@shared/Icons/ArrowIcon';
import ItemProgressBar from '@shared/Ui/ProgressBars/ItemProgressBar/ItemProgressBar';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { SearchInput } from '@shared/Ui/CustomInputs/SearchInput/SearchInput';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@configs/AppRoutes';
import { StepsViewModeEnum } from '@shared/Enums/StepsViewModeEnum';
import { useProjectStore } from '../../../../../Stores/ProjectStore';
import ClearButton from '@shared/Ui/Buttons/ClearButton/ClearButton';
import CloseButton from '@shared/Ui/Buttons/CloseButton/CloseButton';
import { ItemTypeViewModel } from '@viewModels/ItemTypeViewModel/ItemTypeViewModel';

interface Props {
    itemType: ItemTypeEntity;
    searchValue: string;
    changeSearchValue: (searchValue: string) => void;
    changeSortBy: (sortBy: SortParamType) => void;
}

export const SelectionItemsHeaderMobile: React.FC<Props> = ({
    itemType,
    searchValue,
    changeSearchValue,
    changeSortBy,
}) => {
    const { qty, unit } = itemType;
    const { projectId, viewMode } = useParams();

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const initialSort =
        (searchParams.get('sort') as SortParamType) || SortParamEnum.ASC;
    const [sortView, setSortView] = useState<SortParamType>(initialSort);

    const itemTypeViewModel = new ItemTypeViewModel();

    const { setIsMobile } = useProjectStore();

    const totalSelectedItemsQty =
        itemTypeViewModel.calculateTotalQuantityFromItemType(itemType);

    useEffect(() => {
        if (sortView !== searchParams.get('sort')) {
            setSearchParams({ sort: sortView });
            changeSortBy(sortView);
        }
    }, [sortView, searchParams, setSearchParams]);

    const handleClearItemsQuantity = () => {
        itemTypeViewModel.clearItemTypeQuantity(itemType);
        notifySuccessCustom('All input fields have been reset.');
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) =>
        void changeSearchValue(e.target.value);

    const handleSortByAsc = () => {
        setSortView(SortParamEnum.ASC);
    };

    const handleSortByDesc = () => {
        setSortView(SortParamEnum.DESC);
    };

    const handleClose = () => {
        setIsMobile(true);
        if (viewMode === StepsViewModeEnum.PRESENTATION) {
            navigate(ROUTES.STEP2.getPath(StepsViewModeEnum.PRESENTATION));
        }
        navigate(ROUTES.STEP2.getPath(StepsViewModeEnum.PROJECT, projectId));
    };

    return (
        <div className={css.header}>
            <CloseButton onClick={handleClose} />
            <h3 className={css.title}>Please, select your items</h3>

            <ItemProgressBar
                total={qty}
                current={totalSelectedItemsQty}
                progressBarClassName={css.progressBar}
                progressScalePart={css.scale}
                progressTextClassName={css.progressText}
                progressScaleFill={css.scaleFill}
                unit={unit}
                divider={' of '}
            />

            <div className={css.searchInputWrapper}>
                <SearchInput
                    value={searchValue}
                    onChange={handleChangeSearch}
                />
                <ClearButton
                    onClick={handleClearItemsQuantity}
                    extraClassName={css.btn}
                />
            </div>

            <div className={css.sortBtnWrapper}>
                <BasicButton
                    onClick={handleSortByAsc}
                    className={css.sortBtn}
                    extraClassName={
                        sortView === SortParamEnum.ASC ? css.active : ''
                    }
                >
                    <span className={css.btnLabel}>
                        From cheap to expensive
                    </span>
                    <ArrowIcon width={12} height={7} />
                </BasicButton>
                <BasicButton
                    onClick={handleSortByDesc}
                    className={css.sortBtn}
                    extraClassName={
                        sortView === SortParamEnum.DESC ? css.active : ''
                    }
                >
                    <span className={css.btnLabel}>
                        From expensive to cheap
                    </span>
                    <ArrowIcon
                        iconClassName={css.sortDescBtnArrow}
                        width={12}
                        height={7}
                    />
                </BasicButton>
            </div>
        </div>
    );
};
